if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        const formData = new FormData(this.form);
        const purchaseLimit = Number.parseInt(this.dataset.purchaseLimit || '0', 10);
        const productId = Number.parseInt(this.dataset.productId || '0', 10);
        const variantId = Number.parseInt(formData.get('id') || '0', 10);
        const variantLimitEntry = Array.from(this.querySelectorAll('[data-purchase-limit-variant]')).find(
          (entry) => Number.parseInt(entry.dataset.purchaseLimitVariant || '0', 10) === variantId
        );
        const variantPurchaseLimit = Number.parseInt(variantLimitEntry?.dataset.purchaseLimit || '0', 10);
        const requestedQuantity = Number.parseInt(formData.get('quantity') || '1', 10);

        if (variantPurchaseLimit > 0 || (purchaseLimit > 0 && productId > 0)) {
          try {
            const cartResponse = await fetch(`${routes.cart_url}.js`, {
              headers: { Accept: 'application/json' },
            });
            const cart = await cartResponse.json();

            if (variantPurchaseLimit > 0) {
              const currentVariantQuantity = cart.items
                .filter((item) => Number(item.variant_id) === variantId)
                .reduce((total, item) => total + Number(item.quantity || 0), 0);
              const variantLabel = variantLimitEntry?.dataset.purchaseLimitLabel || 'this edition';

              if (currentVariantQuantity + requestedQuantity > variantPurchaseLimit) {
                this.handleErrorMessage(
                  `Limit ${variantPurchaseLimit} ${variantLabel} per order. Your cart already contains ${currentVariantQuantity}.`
                );
                return;
              }
            } else {
              const currentProductQuantity = cart.items
                .filter((item) => Number(item.product_id) === productId)
                .reduce((total, item) => total + Number(item.quantity || 0), 0);

              if (currentProductQuantity + requestedQuantity > purchaseLimit) {
                this.handleErrorMessage(
                  `Limit ${purchaseLimit} copies total per customer across all editions. Your cart already contains ${currentProductQuantity}.`
                );
                return;
              }
            }

            if (requestedQuantity < 1) {
              this.handleErrorMessage(
                'Please select at least one copy.'
              );
              return;
            }
          } catch (error) {
            console.error('Unable to verify the product purchase limit.', error);
          }
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
